/**
 * Tiny shared helpers for the GameScene DOM modals. Each modal owns its
 * own overlay div + lifecycle; these helpers just remove the most
 * repetitive style + event-wiring boilerplate.
 */

export interface ModalShell {
  /** The full-viewport backdrop. Click outside the panel closes the modal. */
  overlay: HTMLDivElement;
  /** The styled inner panel. Append modal content here. */
  panel: HTMLDivElement;
  /** Tear down the overlay + ESC listener. Idempotent. */
  close: () => void;
}

export interface ModalShellOptions {
  /** Panel max-width CSS, e.g. "min(560px, 92vw)". */
  width: string;
  /** Optional max-height (defaults to "92vh"). */
  maxHeight?: string;
  /** Backdrop CSS background. Defaults to a soft dark scrim. */
  backdrop?: string;
  /** Panel CSS background. Defaults to the std purple gradient. */
  panelBackground?: string;
  /** Panel border. Defaults to "1px solid #553388". */
  panelBorder?: string;
  /** Optional callback fired exactly once on close (Esc, X button, or
   *  backdrop click). */
  onClose?: () => void;
}

/**
 * Build the overlay + panel skeleton used by every GameScene modal.
 * Wires Escape + outside-click to close. Caller must `appendChild` to
 * the panel and call `document.body.appendChild(overlay)` themselves.
 */
export function createModalShell(opts: ModalShellOptions): ModalShell {
  const overlay = document.createElement('div');
  Object.assign(overlay.style, {
    position: 'fixed', inset: '0', zIndex: '120',
    background: opts.backdrop ?? 'rgba(0,0,0,0.6)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  } as Partial<CSSStyleDeclaration>);

  const panel = document.createElement('div');
  Object.assign(panel.style, {
    width: opts.width, maxHeight: opts.maxHeight ?? '92vh',
    padding: '16px',
    background: opts.panelBackground ?? '#1b0f30',
    border: opts.panelBorder ?? '1px solid #553388',
    borderRadius: '10px',
    color: '#eee', font: '13px system-ui, sans-serif',
    display: 'flex', flexDirection: 'column', gap: '10px',
  } as Partial<CSSStyleDeclaration>);
  overlay.appendChild(panel);

  let closed = false;
  const close = (): void => {
    if (closed) return;
    closed = true;
    document.removeEventListener('keydown', onKey);
    if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
    if (opts.onClose) opts.onClose();
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') { e.preventDefault(); close(); }
  };
  document.addEventListener('keydown', onKey);

  // Touch-ghost-click guard: when the modal mounts in response to a tap,
  // mobile browsers fire the synthesized `click` (and sometimes a stray
  // `pointerdown`) at the touch coordinates AFTER mount. The tap that
  // opened the modal was almost certainly NOT over the centered panel,
  // so without protection it lands on the overlay backdrop and the
  // "click outside to close" handler dismisses the modal immediately.
  // We swallow pointer/click events on BOTH the overlay and the panel
  // for a short window so the user's first deliberate tap is the one
  // that actually takes effect.
  const SWALLOW_MS = 350;
  const openedAt = performance.now();
  const inSwallowWindow = (): boolean => performance.now() - openedAt < SWALLOW_MS;
  const swallow = (e: Event): void => {
    if (inSwallowWindow()) {
      e.stopPropagation();
      // Don't preventDefault on touchstart/pointerdown — that breaks
      // scrolling. Only block the synthesized click.
      if (e.type === 'click') e.preventDefault();
    }
  };
  panel.addEventListener('click', swallow, { capture: true });
  panel.addEventListener('pointerdown', swallow, { capture: true });
  overlay.addEventListener('click', swallow, { capture: true });
  overlay.addEventListener('pointerdown', swallow, { capture: true });

  // Backdrop-to-close — registered AFTER the swallow listener so the
  // capture-phase swallow above runs first and can stopPropagation
  // during the open window.
  overlay.addEventListener('click', (e) => {
    if (inSwallowWindow()) return;
    if (e.target === overlay) close();
  });

  return { overlay, panel, close };
}

/** Apply the standard pill button style. `primary=true` uses the brand
 *  purple tint; otherwise neutral. */
export function styleButton(b: HTMLButtonElement, primary = false): void {
  Object.assign(b.style, {
    padding: '6px 14px',
    background: primary ? '#3a1f6e' : '#221634',
    color: '#eee',
    border: '1px solid ' + (primary ? '#7755bb' : '#444'),
    borderRadius: '4px',
    cursor: 'pointer', font: 'inherit',
  } as Partial<CSSStyleDeclaration>);
}
